import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2 } from "lucide-react";

export const DEFAULT_CHECKS = [
  { id: "water", label: "Water pressure & hot water" },
  { id: "cell", label: "Cell signal inside" },
  { id: "noise", label: "Street / neighbor noise" },
  { id: "parking", label: "Parking & guest parking" },
  { id: "storage", label: "Storage & closet space" },
  { id: "light", label: "Natural light & windows" },
  { id: "smell", label: "Any odd smells (damp, mold, pets)" },
  { id: "systems", label: "Outlets, switches & AC working" },
];

export function TourChecklist({ tour }) {
  const qc = useQueryClient();
  const mine = tour.my_checks || {};
  const done = mine.done || {};
  const custom = mine.custom || [];
  const [newItem, setNewItem] = useState("");

  const save = useMutation({
    mutationFn: async (next) => (await api.put(`/buyer/tours/${tour.id}/checks`, next)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tours"] }),
  });

  const toggle = (id) => save.mutate({ done: { ...done, [id]: !done[id] }, custom });
  const addCustom = () => {
    const label = newItem.trim();
    if (!label) return;
    const item = { id: `c_${Date.now()}`, label };
    save.mutate({ done, custom: [...custom, item] });
    setNewItem("");
  };
  const removeCustom = (id) => {
    const nextDone = { ...done };
    delete nextDone[id];
    save.mutate({ done: nextDone, custom: custom.filter((c) => c.id !== id) });
  };

  const items = [...DEFAULT_CHECKS, ...custom];

  return (
    <div data-testid={`tour-checklist-${tour.id}`}>
      <div className="space-y-1">
        {items.map((it) => {
          const isCustom = custom.some((c) => c.id === it.id);
          return (
            <div key={it.id} className="flex items-center gap-2 py-1">
              <label className="flex items-center gap-2 text-sm cursor-pointer flex-1">
                <input type="checkbox" checked={!!done[it.id]} onChange={() => toggle(it.id)} className="h-4 w-4 accent-primary" data-testid={`check-${tour.id}-${it.id}`} />
                <span className={done[it.id] ? "line-through text-muted-foreground" : ""}>{it.label}</span>
              </label>
              {isCustom && (
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeCustom(it.id)} data-testid={`check-remove-${it.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex gap-2 mt-2">
        <Input placeholder="Add your own thing to check…" value={newItem} onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") addCustom(); }} className="h-9 flex-1" data-testid={`check-add-input-${tour.id}`} />
        <Button size="sm" className="h-9 rounded-full gap-1" disabled={!newItem.trim()} onClick={addCustom} data-testid={`check-add-btn-${tour.id}`}>
          <Plus className="h-4 w-4" /> Add
        </Button>
      </div>
      <p className="text-[11px] text-muted-foreground mt-2">Your own private checklist for this home — tick items off while you're there.</p>
    </div>
  );
}
